import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getContentById } from '../data/content';
import { getCategoryPhotoObject } from '../lib/unsplash';
import UnsplashImage from '../components/UnsplashImage';
import toast from 'react-hot-toast';
import Icon from '../components/shared/Icon';
import Comments from '../components/Comments';
import ClapButton from '../components/shared/ClapButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUtensils } from '@fortawesome/free-solid-svg-icons';
import { PageHeader } from '../design-system';

export default function RecipeDetail() {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFirestoreContent, setIsFirestoreContent] = useState(false);
  const [photoObject, setPhotoObject] = useState(null);

  // Try to load from Firestore first (approved user submissions)
  useEffect(() => {
    if (!recipeId) {
      setLoading(false);
      return;
    }

    const contentRef = doc(db, 'content', recipeId);
    const unsubscribe = onSnapshot(
      contentRef,
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          if (data.type === 'recipe') {
            // Convert Firestore content to recipe format
            setRecipe({
              id: doc.id,
              title: data.title,
              description: data.description,
              time: `${data.content.prepTime || ''} ${data.content.cookTime || ''}`.trim() || 'N/A',
              difficulty: data.content.difficulty || 'Medium',
              image: data.content.imageUrl ? null : '🍞',
              imageUrl: data.content.imageUrl,
              imageSearchQuery: data.imageSearchQuery,
              culturalContext: data.content.culturalContext,
              ingredients: data.content.ingredients || [],
              instructions: data.content.instructions || [],
            });
            // Use stored unsplashPhoto if available
            if (data.unsplashPhoto) {
              setPhotoObject(data.unsplashPhoto);
            }
            // Store uploaded image info
            setRecipe(prev => ({
              ...prev,
              unsplashPhoto: data.unsplashPhoto,
              uploadedImageUrl: data.uploadedImageUrl,
              imageCaption: data.imageCaption
            }));
            setIsFirestoreContent(true);
            setLoading(false);
            return;
          }
        }
        
        // Not found in Firestore, try static content
        const staticRecipe = getContentById('recipes', recipeId);
        if (staticRecipe) {
          setRecipe(staticRecipe);
          setIsFirestoreContent(false);
        }
        setLoading(false);
      },
      (error) => {
        // Error loading from Firestore, try static content
        const staticRecipe = getContentById('recipes', recipeId);
        if (staticRecipe) {
          setRecipe(staticRecipe);
          setIsFirestoreContent(false);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [recipeId]);

  // Load photo object if not already set
  useEffect(() => {
    if (recipe && !photoObject) {
      getCategoryPhotoObject('recipes', recipe, 0).then(photo => {
        if (photo) {
          setPhotoObject(photo);
        }
      });
    }
  }, [recipe, photoObject]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-text-secondary">Loading recipe...</div>
        </div>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-white flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4"><Icon name="🔍" size="4x" className="text-text-secondary" /></div>
          <div className="text-lg text-text-secondary">Recipe not found</div>
          <button
            onClick={() => navigate('/explore')}
            className="mt-4 px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-primary to-primary-light text-white hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            Back to Explore
          </button>
        </div>
      </div>
    );
  }

  const handleAddToExperience = () => {
    // TODO: Implement adding to experience shopping list
    toast.success('Recipe added to shopping list!');
  };

  return (
    <div className="min-h-screen bg-white pt-14 pb-20 md:pt-0 md:pb-0">
      <PageHeader
        title={recipe.title}
        onBack={() => navigate('/explore')}
        maxWidth="896px"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-8 md:py-10">

        <div className="bg-white rounded-card shadow-card overflow-hidden">
          {/* Hero Section */}
          <div className="w-full h-[200px] sm:h-[300px] bg-surface flex items-center justify-center rounded-t-card overflow-hidden relative">
            {recipe.unsplashPhoto ? (
              <UnsplashImage
                photo={recipe.unsplashPhoto}
                alt={recipe.title}
                className="w-full h-full object-cover"
                triggerDownloadOnClick={false}
              />
            ) : recipe.uploadedImageUrl ? (
              <div className="relative w-full h-full">
                <img 
                  src={recipe.uploadedImageUrl} 
                  alt={recipe.title}
                  className="w-full h-full object-cover"
                />
                {recipe.imageCaption && (
                  <div className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm px-2 py-1 rounded text-xs text-white">
                    {recipe.imageCaption}
                  </div>
                )}
              </div>
            ) : photoObject ? (
              <UnsplashImage
                photo={photoObject}
                alt={recipe.title}
                className="w-full h-full object-cover"
                triggerDownloadOnClick={false}
              />
            ) : recipe.imageUrl ? (
              <img 
                src={recipe.imageUrl} 
                alt={recipe.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <Icon name={recipe.image || '🍞'} size="5x" className="text-primary" />
            )}
          </div>

          <div className="p-6 sm:p-8 md:p-10">
            {/* Title and Meta */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">
                  {recipe.difficulty} • {recipe.time}
                </div>
              </div>
              <h1 className="font-sans text-2xl sm:text-3xl md:text-4xl font-semibold text-text-primary mb-3 tracking-tight">
                {recipe.title}
              </h1>
              <p className="text-base sm:text-lg text-text-secondary leading-relaxed">
                {recipe.description}
              </p>
            </div>

            {/* Cultural Context */}
            <div className="bg-surface rounded-xl p-5 sm:p-6 mb-6 sm:mb-8">
              <div className="text-sm font-semibold text-text-primary mb-2">Cultural Context</div>
              <p className="text-sm sm:text-base text-text-secondary leading-relaxed">
                {recipe.culturalContext}
              </p>
            </div>

            {/* Ingredients */}
            <div className="mb-6 sm:mb-8">
              <h2 className="font-sans text-xl sm:text-2xl md:text-3xl font-semibold text-text-primary mb-4 sm:mb-5 tracking-tight">
                Ingredients
              </h2>
              <div className="space-y-2 sm:space-y-3">
                {recipe.ingredients.map((ingredient, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white border border-border rounded-lg"
                  >
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-primary-light text-primary flex items-center justify-center text-xs sm:text-sm font-semibold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 text-sm sm:text-base text-text-primary">
                      {ingredient.name}
                    </div>
                    <div className="text-sm sm:text-base font-semibold text-text-secondary">
                      {ingredient.amount}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Instructions */}
            <div className="mb-6 sm:mb-8">
              <h2 className="font-sans text-xl sm:text-2xl md:text-3xl font-semibold text-text-primary mb-4 sm:mb-5 tracking-tight">
                Instructions
              </h2>
              <div className="space-y-4 sm:space-y-5">
                {recipe.instructions.map((step, index) => (
                  <div
                    key={index}
                    className="flex gap-4 sm:gap-5 p-4 sm:p-5 bg-white border border-border rounded-lg"
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary text-white flex items-center justify-center text-base sm:text-lg font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 text-sm sm:text-base text-text-primary leading-relaxed pt-1">
                      {step}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mb-6">
              <button
                onClick={handleAddToExperience}
                className="flex-1 px-6 py-3 sm:py-4 rounded-lg font-semibold bg-primary text-white hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover"
              >
                ➕ Add to Experience Shopping List
              </button>
              <ClapButton 
                contentId={recipeId} 
                contentType="recipe" 
                size="lg"
              />
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <Comments contentId={recipeId} contentType="recipe" />
      </div>
    </div>
  );
}

